import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  IHouseEvent,
  IEvent,
  IEventId,
  IHouseEventParticipants,
  ITaskProgress,
} from '../../../models/IHouseEvent';
import moment from 'moment';
import { UploadHouseEventsResponse } from 'src/app/features/admin/house-and-well-being-events/house-and-well-being-events.interface';
import { get, post, del, patch } from 'aws-amplify/api';
@Injectable({
  providedIn: 'root',
})
export class HouseEventService {
  constructor(private http: HttpClient) {}
  // start house-and-well-being-events.component
  public getHouseEvent() {
    const op = get({
      apiName: 'portal',
      path: 'house-events',
    });
    return from(op.response.then(async (r) => (await r.body.json()) as unknown as IHouseEvent[])).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IHouseEvent[]) => {
        data = data.map((s: IHouseEvent) => {
          s.isEditable = false;
          s.readMore = false;
          s.startTime = moment(s.startDate).format('HH:mm');
          s.endTime = moment(s.endDate).format('HH:mm');
          return s;
        });
        data.sort((a, b) => {
          const aStartDate: Date = new Date(a.startDate);
          const bStartDate: Date = new Date(b.startDate);
          const aEndDate: Date = new Date(a.endDate);
          const bEndDate: Date = new Date(b.endDate);
          const endDateValue = aEndDate < bEndDate ? -1 : aEndDate > bEndDate ? 1 : 0;
          return aStartDate < bStartDate ? -1 : aStartDate > bStartDate ? 1 : endDateValue;
        });
        return data as IHouseEvent[];
      })
    );
  }
  public getHouseEventImages() {
    const op = get({
      apiName: 'portal',
      path: 'house-events/images',
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any[])).pipe(
      catchError((error) => throwError(() => error)),
      map((data: any[]) => data)
    );
  }
  public getAllEvents() {
    const op = get({
      apiName: 'portal',
      path: 'house-events/tags',
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any[])).pipe(
      catchError((error) => throwError(() => error)),
      map((data: any[]) => data)
    );
  }
  public addHouseEvent(data: any): Observable<IEvent> {
    const body = JSON.parse(JSON.stringify(data)) as Record<string, any>;
    const op = post({
      apiName: 'portal',
      path: 'house-events',
      options: { body },
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((res: any) => res as IEvent)
    );
  }
public fileUploadHouseEvent(data: any): Observable<UploadHouseEventsResponse[]> {
  const body = JSON.parse(JSON.stringify(data));
  const op = post({
    apiName: 'portal',
    path: 'house-events/file-upload',
    options: { body },
  });
  return from(
    op.response.then(async r => await r.body.json())
  ).pipe(
    catchError(error => throwError(() => error)),
    map(res => res as unknown as UploadHouseEventsResponse[] )
  );
}
public fileUploadEventParticipants(data: any, activityId: any): Observable<string> {
  const body = JSON.parse(JSON.stringify(data)) as Record<string, any>;
  const op = post({
    apiName: 'portal',
    path: `house-events/participants/file-upload?eventId=${activityId}`,
    options: { body },
  });
  return from(
    op.response.then(async (r) => {
      // backend returns plain string taskId
      const taskId = await r.body.text();
      return taskId?.trim();
    })
  );
}
  public updateHouseEvent(id: IEventId, data: any): Observable<IEvent> {
    const body = JSON.parse(JSON.stringify(data)) as Record<string, any>;
    const op = patch({
      apiName: 'portal',
      path: 'house-events',
      options: { body },
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((res: any) => res as IEvent)
    );
  }
  public deleteHouseEvent(id: IEventId): Observable<IEventId> {
    const op = del({
      apiName: 'portal',
      path: `house-events?id=${id}`,
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as IEventId)
    );
  }
  public getHouseEventParticipants(id: IEventId) {
    const op = get({
      apiName: 'portal',
      path: `house-events?id=${id}`,
    });
    return from(op.response.then(async (r) => (await r.body.json()) as unknown as IHouseEventParticipants[])).pipe(
      catchError((error) => throwError(() => error)),
      map((data: IHouseEventParticipants[]) => data as IHouseEventParticipants[])
    );
  }
  public deleteHouseEventParticipant(data: any): Observable<IEventId> {
    const body = JSON.parse(JSON.stringify(data)) as Record<string, any>;
    const op = del({
      apiName: 'portal',
      path: 'house-events/participants',
      options: { body },
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as IEventId)
    );
  }
  public addHouseEventParticipant(data: any): Observable<IEventId> {
    const body = JSON.parse(JSON.stringify(data)) as Record<string, any>;
    const op = post({
      apiName: 'portal',
      path: 'house-events/participants',
      options: { body },
    });
    return from(op.response.then(async (r) => (await r.body) as any)).pipe(
      map((res: any) => res as IEventId)
    );
  }
  // end house-and-well-being-events.component
  public taskProgress(taskId: string): Observable<ITaskProgress> {
    const op = get({
      apiName: 'portal',
      path: `task?id=${taskId}`,
    });
    return from(op.response.then(async (r) => (await r.body.json()) as any)).pipe(
      catchError((error) => throwError(() => error)),
      map((res: any) => {
        return res
          ? (res as ITaskProgress)
          : ({
              id: taskId,
              status: 'InProgress',
            } as any); // default when API returns empty with success
      })
    );
  }
}